// [MYSALES-REPORT 2026-10-28, user] NEW FILE — the My Sales "Download" (the button at the left end of the Leads to Sales
// heading). From the server's report — every active sales rep the user may see, each with the table the screen shows
// for that rep, plus the total — it makes:
//   • Excel: two tabs, "Leads to Sales" and "Overall Sales". Each is ONE big table: the reps down column A, the 6 months
//     across the top (each month a group of that view's columns), a Total group, and the total line at the bottom. Plain,
//     as the user asked: bold headings only, no colours. Real numbers (Excel can add them up) with the screen's rounding.
//   • PDF: "My Sales Report" on top, then the All Sales Rep block, then each rep A → Z — the name, his Leads to Sales table
//     and his Overall Sales table (each with its Total row), kept together on one page. A soft blue heading row, lines
//     and name band; the data plain.
// User's rules (2026-10-28): only reps with data; admin / senior = every active rep, a rep = only himself (the server
// decides who); the PDF starts with All Sales Rep.
// Nothing is calculated here but the rounding the screen does (MySalesPage fmtCurrency / fmtCurrencyWhole / fmtPct /
// fmtMonthYY), so the file says exactly what the screen says.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { CellHookData } from 'jspdf-autotable';
import { zip, esc, colName, downloadXlsx } from './xlsx';
import type { MySalesReport, MySalesLeadsPerf, MySalesLeadsPerfRow } from '../services/api';

export type MySalesReportFormat = 'xlsx' | 'pdf';

/* ── The screen's formats ──────────────────────────────────────────────────────────────────────────────────────── */

const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const WHOLE_FMT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MON_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const num = (v: number | undefined) => Number(v) || 0;
// The same text the screen prints: "-$200.00" (Leads to Sales money), "-$200" (Overall Sales money), "2.45%", "1,673".
const moneyText = (v: number | undefined) => { const x = num(v); return `${Math.round(x * 100) < 0 ? '-' : ''}$${MONEY_FMT.format(Math.abs(x))}`; };
const wholeText = (v: number | undefined) => { const x = num(v); const r = Math.round(Math.abs(x)); return `${x < 0 && r > 0 ? '-' : ''}$${WHOLE_FMT.format(r)}`; };
const pctText = (v: number | undefined) => `${num(v).toFixed(2)}%`;
const intText = (v: number | undefined) => num(v).toLocaleString('en-US');

type Kind = 'int' | 'pct' | 'money' | 'whole';
const TEXT: Record<Kind, (v: number | undefined) => string> = { int: intText, pct: pctText, money: moneyText, whole: wholeText };
/** The number behind a screen cell, as Excel stores it: its cell then reads exactly like the screen. */
function shownValue(kind: Kind, v: number | undefined): number {
  if (kind === 'int') return num(v);
  if (kind === 'pct') return Number(num(v).toFixed(2)) / 100;
  const t = TEXT[kind](v);
  const n = Number(t.replace(/[-$,]/g, ''));
  return t.startsWith('-') ? -n : n;
}

const ymParts = (ym: string) => ym.split('-').map(Number);
const monthLabel = (ym: string) => { const [y, m] = ymParts(ym); return `${MON[m - 1]} ${y}`; };   // "Apr 2026", as the screen
/** "April – September 2026" (or "November 2026 – April 2027"). */
function periodTitle(months: string[]): string {
  const [fy, fm] = ymParts(months[0]);
  const [ly, lm] = ymParts(months[months.length - 1]);
  return fy === ly ? `${MON_FULL[fm - 1]} – ${MON_FULL[lm - 1]} ${ly}` : `${MON_FULL[fm - 1]} ${fy} – ${MON_FULL[lm - 1]} ${ly}`;
}
/** For the file name: "Apr to Sep 2026" (or "Nov 2026 to Apr 2027"). */
function periodFile(months: string[]): string {
  const [fy, fm] = ymParts(months[0]);
  const [ly, lm] = ymParts(months[months.length - 1]);
  return fy === ly ? `${MON[fm - 1]} to ${MON[lm - 1]} ${ly}` : `${MON[fm - 1]} ${fy} to ${MON[lm - 1]} ${ly}`;
}

/* ── The columns: the screen's, view by view ─────────────────────────────────────────────────────────────────── */

interface Col {
  head: string;
  kind: Kind;
  get: (r: MySalesLeadsPerfRow) => number | undefined;
  /** The "No active sales rep" line carries refunds / disputes only: just these columns are filled on it. */
  noRep?: boolean;
}
type PartKey = 'recurring' | 'oneTime' | 'pia' | 'refundsDisputes' | 'disputesWon';
// MySalesPage PERF_PARTS: Recurring + One-Time always; the other three only where someone has an amount in them.
const PARTS: ReadonlyArray<{ key: PartKey; head: string; always: boolean }> = [
  { key: 'recurring', head: 'Recurring New Sales', always: true },
  { key: 'oneTime', head: 'One-Time New Sales', always: true },
  { key: 'pia', head: 'Sales PIA', always: false },
  { key: 'refundsDisputes', head: 'Refund / Dispute', always: false },
  { key: 'disputesWon', head: 'Disputes Won', always: false },
];
const LEADS_COLS: Col[] = [
  { head: 'Leads', kind: 'int', get: (r) => r.leads },
  { head: 'Sales', kind: 'int', get: (r) => r.sales },
  { head: 'Conversion%', kind: 'pct', get: (r) => r.conversion },
  { head: 'In Bank', kind: 'money', get: (r) => r.inBank },
  { head: 'ATV', kind: 'money', get: (r) => r.atv },
];
const anyMonth = (p: MySalesLeadsPerf, get: (r: MySalesLeadsPerfRow) => number | undefined) =>
  p.rows.some((r) => Math.abs(num(get(r))) >= 0.005);
/** The Overall Sales columns for these tables: an optional part only when one of them has it in a month. */
function overallCols(perfs: MySalesLeadsPerf[]): Col[] {
  const parts = PARTS.filter((p) => p.always || perfs.some((perf) => anyMonth(perf, (r) => r.overall[p.key])));
  return [
    { head: 'Leads', kind: 'int', get: (r) => r.leads },
    { head: 'Sales', kind: 'int', get: (r) => r.overall.sales },
    { head: 'Conversion%', kind: 'pct', get: (r) => r.overall.conversion },
    ...parts.map((p): Col => ({ head: p.head, kind: 'whole', get: (r) => r.overall[p.key], noRep: p.key === 'refundsDisputes' || p.key === 'disputesWon' })),
    { head: 'Net In-Bank', kind: 'whole', get: (r) => r.overall.inBank, noRep: true },
    { head: 'ATV', kind: 'whole', get: (r) => r.overall.atv },
  ];
}

// "Don't show a rep who has no data": per view — Leads to Sales needs a lead, a sale or money in a month; Overall Sales
// a lead, a sale or any money.
const hasLeadsData = (p: MySalesLeadsPerf) => anyMonth(p, (r) => r.leads) || anyMonth(p, (r) => r.sales) || anyMonth(p, (r) => r.inBank);
const hasOverallData = (p: MySalesLeadsPerf) => anyMonth(p, (r) => r.leads) || anyMonth(p, (r) => r.overall.sales)
  || anyMonth(p, (r) => r.overall.inBank) || PARTS.some((pt) => anyMonth(p, (r) => r.overall[pt.key]));

/* ── What goes in the files ──────────────────────────────────────────────────────────────────────────────────── */

interface Line { name: string; perf: MySalesLeadsPerf; kind: 'rep' | 'noRep' | 'total' }
interface Plan {
  months: string[];
  leads: Line[];     // Excel "Leads to Sales": the reps with data, then the total line
  overall: Line[];   // Excel "Overall Sales": the reps with data, the "No active sales rep" line, the total line
  pdf: Line[];       // PDF: the total block first, then every rep with data
  base: string;      // file name without the extension
}

/** [MYSALES-REPORT-PICK 2026-10-28, user] The reps the file can carry — every one with data, A → Z — for the pop-up
 *  where the user ticks who goes in. */
export function reportPeople(report: MySalesReport): string[] {
  return report.reps.filter((r) => hasLeadsData(r.perf) || hasOverallData(r.perf)).map((r) => r.name);
}

/** [MYSALES-REPORT-PICK 2026-10-28, user] The ticked reps added up — the server's own Total maths (computeLeadsPerf):
 *  every figure summed month by month (the Total row sums the months), Conversion% and ATV worked out again from the
 *  sums. Used when the user leaves someone out, so the total line is the total of the reps in the file. */
function sumPerf(perfs: MySalesLeadsPerf[], title: string): MySalesLeadsPerf {
  const add = (rows: MySalesLeadsPerfRow[], month: string): MySalesLeadsPerfRow => {
    const s = (get: (r: MySalesLeadsPerfRow) => number | undefined) => rows.reduce((a, r) => a + num(get(r)), 0);
    const leads = s((r) => r.leads);
    const sales = s((r) => r.sales);
    const inBank = s((r) => r.inBank);
    const oSales = s((r) => r.overall.sales);
    const oInBank = s((r) => r.overall.inBank);
    return {
      month, leads, sales, inBank,
      conversion: leads ? (sales / leads) * 100 : 0,
      atv: sales ? inBank / sales : 0,
      overall: {
        sales: oSales, inBank: oInBank,
        conversion: leads ? (oSales / leads) * 100 : 0,
        atv: oSales ? oInBank / oSales : 0,
        recurring: s((r) => r.overall.recurring), oneTime: s((r) => r.overall.oneTime), pia: s((r) => r.overall.pia),
        refundsDisputes: s((r) => r.overall.refundsDisputes), disputesWon: s((r) => r.overall.disputesWon),
      },
    };
  };
  const rows = perfs[0].rows.map((m, i) => add(perfs.map((p) => p.rows[i]), m.month));
  return { title, rows, total: add(rows, ''), overallOk: true, leadsOk: true };
}

function plan(report: MySalesReport, picked?: string[]): Plan | null {
  const perfs = [...report.reps.map((r) => r.perf), report.total, report.noRep].filter((p): p is MySalesLeadsPerf => !!p);
  // The server answers 503 rather than send an unmeasured figure; this keeps a file from ever being made from one.
  if (perfs.some((p) => p.leadsOk === false || p.overallOk === false)) {
    throw new Error('Some sales data could not be read just now. Please try again in a minute.');
  }
  // [MYSALES-REPORT-PICK 2026-10-28, user] Only the ticked reps. Everyone with data ticked = the file as before (the
  // screen's All Sales Rep total and its "No active sales rep" line); someone left out = the total of the ticked reps
  // only, and no "No active sales rep" line (that money is on none of the people picked).
  const chosen = picked ? new Set(picked) : null;
  const leftOut = !!chosen && reportPeople(report).some((n) => !chosen.has(n));
  const reps: Line[] = report.reps.filter((r) => !chosen || chosen.has(r.name)).map((r) => ({ name: r.name, perf: r.perf, kind: 'rep' }));
  const pickedPerfs = reps.filter((l) => hasLeadsData(l.perf) || hasOverallData(l.perf)).map((l) => l.perf);
  const total: Line | null = leftOut
    ? (pickedPerfs.length > 1 ? { name: 'Total (selected reps)', perf: sumPerf(pickedPerfs, 'Total (selected reps)'), kind: 'total' } : null)
    : report.total
      ? { name: report.canSelectAll ? 'All Sales Rep' : 'Team Total', perf: report.total, kind: 'total' }
      : null;
  const noRep: Line | null = report.noRep && !leftOut ? { name: 'No active sales rep', perf: report.noRep, kind: 'noRep' } : null;
  const leadsReps = reps.filter((l) => hasLeadsData(l.perf));
  const overallReps = reps.filter((l) => hasOverallData(l.perf));
  const pdfReps = reps.filter((l) => hasLeadsData(l.perf) || hasOverallData(l.perf));
  if (!pdfReps.length && !noRep) return null;   // nobody has anything in these months
  // A single rep's own file: his name in the file name too.
  const who = !report.total && report.reps.length === 1 ? ` - ${report.reps[0].name.replace(/[\\/:*?"<>|]+/g, ' ').trim()}` : '';
  return {
    months: report.months,
    leads: leadsReps.length ? [...leadsReps, ...(total ? [total] : [])] : [],
    overall: overallReps.length || noRep ? [...overallReps, ...(noRep ? [noRep] : []), ...(total ? [total] : [])] : [],
    pdf: [...(total ? [total] : []), ...pdfReps],
    base: `My Sales Report${who} - ${periodFile(report.months)}`,
  };
}

/* ── Excel ───────────────────────────────────────────────────────────────────────────────────────────────────── */

// Cell formats. No fills, no borders, no colours (user): only bold headings and the number formats the screen uses.
const S = { text: 0, bold: 1, group: 2, head: 3, int: 4, pct: 5, money: 6, whole: 7 } as const;
const KIND_STYLE: Record<Kind, number> = { int: S.int, pct: S.pct, money: S.money, whole: S.whole };
const STYLES_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
  + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
  + '<numFmts count="2">'
  + '<numFmt numFmtId="164" formatCode="&quot;$&quot;#,##0.00;-&quot;$&quot;#,##0.00"/>'
  + '<numFmt numFmtId="165" formatCode="&quot;$&quot;#,##0;-&quot;$&quot;#,##0"/>'
  + '</numFmts>'
  + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/><family val="2"/></font>'
  + '<font><b/><sz val="11"/><name val="Calibri"/><family val="2"/></font></fonts>'
  + '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>'
  + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
  + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
  // ⚠ count must equal the number of <xf> entries or Excel refuses the file.
  + '<cellXfs count="8">'
  + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
  + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="center"/></xf>'
  + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>'
  // A column heading over figures sits on their right edge ([XLSX-HEAD-ALIGN 2026-09-30]); a long one wraps at its spaces.
  + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="right" vertical="center" wrapText="1"/></xf>'
  + '<xf numFmtId="3" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="10" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '</cellXfs>'
  + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
  + '</styleSheet>';

interface XCell { v: number | string; s: number; text: string }

function cellXml(ref: string, cell: XCell): string {
  const s = cell.s ? ` s="${cell.s}"` : '';
  if (typeof cell.v === 'number') return `<c r="${ref}"${s}><v>${cell.v}</v></c>`;
  if (cell.v === '') return `<c r="${ref}"${s}/>`;
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${esc(cell.v)}</t></is></c>`;
}

/** Lines a bold heading takes in a column `width` characters wide (Calibri bold runs ~15 % wider than its count). */
function headLines(text: string, width: number): number {
  let lines = 1;
  let used = 0;
  for (const word of text.split(' ')) {
    const w = word.length * 1.15;
    if (!used) used = w;
    else if (used + 1.15 + w <= width - 1) used += 1.15 + w;
    else { lines++; used = w; }
  }
  return lines;
}

/** One tab: title, the two heading rows (months, then the view's columns), the reps, and the closing lines. */
function sheetXml(title: string, months: string[], cols: Col[], lines: Line[], first: boolean): string {
  const rows = new Map<number, Map<number, XCell>>();
  const put = (r: number, c: number, v: number | string, s: number, text = String(v)) => {
    if (!rows.has(r)) rows.set(r, new Map());
    rows.get(r)!.set(c, { v, s, text });
  };
  const merges: string[] = [];
  const groups = [...months.map(monthLabel), 'Total'];
  const lastCol = groups.length * cols.length;   // column A + one group of `cols` per month and for the Total

  put(1, 0, title, S.bold);
  put(3, 0, 'Sales Rep', S.bold);
  put(4, 0, '', S.bold);
  merges.push('A3:A4');
  groups.forEach((g, gi) => {
    const c0 = 1 + gi * cols.length;
    cols.forEach((col, k) => {
      put(3, c0 + k, k === 0 ? g : '', S.group);
      put(4, c0 + k, col.head, S.head);
    });
    merges.push(`${colName(c0)}3:${colName(c0 + cols.length - 1)}3`);
  });

  let r = 5;
  if (!lines.length) put(r++, 0, 'No sales rep has data in these months.', S.text);
  lines.forEach((line, i) => {
    if (line.kind !== 'rep' && i > 0 && lines[i - 1].kind === 'rep') r++;   // one empty row before the closing lines
    put(r, 0, line.name, S.text);
    [...line.perf.rows, line.perf.total].forEach((pr, gi) => cols.forEach((col, k) => {
      if (line.kind === 'noRep' && !col.noRep) return;   // only its refunds / disputes and their net
      const v = col.get(pr);
      put(r, 1 + gi * cols.length + k, shownValue(col.kind, v), KIND_STYLE[col.kind], TEXT[col.kind](v));
    }));
    r++;
  });
  const lastRow = r - 1;

  // Column widths from what the cells show; a heading needs room for its longest word (bold).
  const widths = new Array<number>(lastCol + 1).fill(8);
  rows.forEach((cells, rr) => cells.forEach((cell, c) => {
    if (rr === 1 || rr === 3) return;   // the title and the month headings run over several columns
    const need = rr === 4
      ? Math.ceil(Math.max(...cell.text.split(' ').map((w) => w.length)) * 1.15) + 2
      : cell.text.length + 2;
    widths[c] = Math.max(widths[c], need);
  }));
  widths[0] = Math.max(widths[0], 14);
  let headH = 1;
  rows.get(4)!.forEach((cell, c) => { if (c > 0) headH = Math.max(headH, headLines(cell.text, widths[c])); });

  const sheetRows: string[] = [];
  for (let rr = 1; rr <= lastRow; rr++) {
    const cells = rows.get(rr);
    if (!cells) continue;
    const ht = rr === 4 ? ` ht="${15 * headH + 3}" customHeight="1"` : '';
    const xml = Array.from(cells.entries()).sort((a, b) => a[0] - b[0]).map(([c, cell]) => cellXml(`${colName(c)}${rr}`, cell)).join('');
    sheetRows.push(`<row r="${rr}"${ht}>${xml}</row>`);
  }
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" '
    + 'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
    + `<dimension ref="A1:${colName(lastCol)}${lastRow}"/>`
    // [MYSALES-XLSX-NO-FREEZE 2026-10-28, user] "dont make the rows or columns freeze": no frozen pane (it locked column A and
    // rows 1-4 at B5) — the whole sheet scrolls as one, the cursor on A1.
    + `<sheetViews><sheetView workbookViewId="0"${first ? ' tabSelected="1"' : ''}/></sheetViews>`
    + '<sheetFormatPr defaultRowHeight="15"/>'
    + `<cols>${widths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>`
    + `<sheetData>${sheetRows.join('')}</sheetData>`
    + `<mergeCells count="${merges.length}">${merges.map((m) => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>`
    + '<pageMargins left="0.5" right="0.5" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>'
    + '</worksheet>';
}

function workbook(sheets: Array<{ name: string; xml: string }>): Uint8Array<ArrayBuffer> {
  const types = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
    + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
    + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
    + sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
    + '</Types>';
  const rels = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
    + '</Relationships>';
  const book = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" '
    + 'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
    + '<bookViews><workbookView activeTab="0"/></bookViews>'
    + `<sheets>${sheets.map((s, i) => `<sheet name="${esc(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>`
    + '</workbook>';
  const bookRels = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')
    + `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`
    + '</Relationships>';
  return zip([
    { name: '[Content_Types].xml', text: types },
    { name: '_rels/.rels', text: rels },
    { name: 'xl/workbook.xml', text: book },
    { name: 'xl/_rels/workbook.xml.rels', text: bookRels },
    { name: 'xl/styles.xml', text: STYLES_XML },
    ...sheets.map((s, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, text: s.xml })),
  ]);
}

/** The Excel file, or null when no rep has data in these months. `picked` = the ticked reps (all when left out). */
export function buildMySalesXlsx(report: MySalesReport, picked?: string[]): { name: string; bytes: Uint8Array<ArrayBuffer> } | null {
  const p = plan(report, picked);
  if (!p) return null;
  const period = periodTitle(p.months);
  const ocols = overallCols(p.overall.map((l) => l.perf));
  return {
    name: `${p.base}.xlsx`,
    bytes: workbook([
      { name: 'Leads to Sales', xml: sheetXml(`Leads to Sales · ${period}`, p.months, LEADS_COLS, p.leads, true) },
      { name: 'Overall Sales', xml: sheetXml(`Overall Sales · ${period}`, p.months, ocols, p.overall, false) },
    ]),
  };
}

/* ── PDF ─────────────────────────────────────────────────────────────────────────────────────────────────────── */

type RGB = [number, number, number];
// A soft blue for the headings, the lines and the name bands (user: "subtle blue … and then simply the data").
const NAVY: RGB = [31, 56, 100];        // title, names, heading text
const BLUE: RGB = [47, 85, 151];        // the small "LEADS TO SALES" / "OVERALL SALES" captions
const HEAD_FILL: RGB = [221, 231, 244]; // table heading row
const BAND_FILL: RGB = [236, 242, 250]; // behind a rep's name
const FOOT_FILL: RGB = [243, 247, 252]; // the Total row
const LINE: RGB = [196, 210, 228];      // table lines
const INK: RGB = [33, 37, 41];          // the figures
const MUTED: RGB = [112, 124, 142];     // subtitle, page numbers
const M = { left: 40, right: 40, top: 40, bottom: 44 };
const PAD = { top: 3.5, right: 4, bottom: 3.5, left: 4 };
const FONT_SIZES = [9, 8.5, 8, 7.5, 7];   // the largest one every table fits at is used for the whole file
const LH = 1.15;                          // jsPDF's line height factor (autoTable's rows use it)
const BAND_H = 20;

interface PdfTable { caption: string; head: string[]; body: string[][]; foot: string[] }

function tableFor(perf: MySalesLeadsPerf, cols: Col[], caption: string): PdfTable {
  const cells = (r: MySalesLeadsPerfRow) => cols.map((c) => TEXT[c.kind](c.get(r)));
  return {
    caption,
    head: ['Month', ...cols.map((c) => c.head)],
    body: perf.rows.map((r) => [monthLabel(r.month), ...cells(r)]),
    foot: ['Total', ...cells(perf.total)],
  };
}

/** Each column's narrowest width: its longest heading word (bold), figure or Total (bold), plus the padding. */
function naturalWidths(doc: jsPDF, t: PdfTable, fs: number): number[] {
  doc.setFontSize(fs);
  return t.head.map((h, c) => {
    doc.setFont('helvetica', 'bold');
    let w = Math.max(...h.split(' ').map((word) => doc.getTextWidth(word)), doc.getTextWidth(t.foot[c] ?? ''));
    doc.setFont('helvetica', 'normal');
    for (const row of t.body) w = Math.max(w, doc.getTextWidth(row[c] ?? ''));
    return w + PAD.left + PAD.right + 1;
  });
}

/** The table across the full width: the narrowest widths, the room left shared in proportion. */
function spread(widths: number[], full: number): number[] {
  const sum = widths.reduce((a, b) => a + b, 0);
  return widths.map((w) => (w * full) / sum);
}

/** The PDF, or null when no rep has data in these months. `picked` = the ticked reps (all when left out). */
export function buildMySalesPdf(report: MySalesReport, picked?: string[]): { name: string; doc: jsPDF } | null {
  const p = plan(report, picked);
  if (!p) return null;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const full = pageW - M.left - M.right;
  const period = periodTitle(p.months);
  doc.setProperties({ title: `My Sales Report · ${period}` });

  // Each block: the name, then its Leads to Sales and Overall Sales tables (the screen's columns for that rep).
  const blocks = p.pdf.map((line) => ({
    name: line.name,
    tables: [
      ...(hasLeadsData(line.perf) ? [tableFor(line.perf, LEADS_COLS, 'Leads to Sales')] : []),
      ...(hasOverallData(line.perf) ? [tableFor(line.perf, overallCols([line.perf]), 'Overall Sales')] : []),
    ],
  }));
  const tables = blocks.flatMap((b) => b.tables);
  const fs = FONT_SIZES.find((size) => tables.every((t) => naturalWidths(doc, t, size).reduce((a, b) => a + b, 0) <= full))
    ?? FONT_SIZES[FONT_SIZES.length - 1];
  const rowH = fs * LH + PAD.top + PAD.bottom;
  const widthsOf = (t: PdfTable) => spread(naturalWidths(doc, t, fs), full);
  const tableH = (t: PdfTable) => {
    const w = widthsOf(t);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fs);
    const lines = Math.max(...t.head.map((h, c) => doc.splitTextToSize(h, w[c] - PAD.left - PAD.right).length));
    return lines * fs * LH + PAD.top + PAD.bottom + (t.body.length + 1) * rowH;
  };
  const blockH = (b: typeof blocks[number]) => BAND_H + 10 + b.tables.reduce((s, t) => s + 10 + tableH(t) + 14, 0);

  // Page 1: the title.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...NAVY);
  doc.text('My Sales Report', pageW / 2, 54, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...MUTED);
  doc.text(`Leads to Sales and Overall Sales · ${period}`, pageW / 2, 70, { align: 'center' });
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.8);
  doc.line(M.left, 82, pageW - M.right, 82);
  let y = 98;
  let pageTop = y;   // the first block on a page never moves on, so a page is never left empty

  for (const b of blocks) {
    // A rep's tables stay together: a block that does not fit what is left of the page starts the next one.
    if (y > pageTop && y + blockH(b) > pageH - M.bottom) { doc.addPage(); y = M.top; pageTop = y; }
    doc.setFillColor(...BAND_FILL);
    doc.roundedRect(M.left, y, full, BAND_H, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    doc.text(b.name, M.left + 8, y + 13.8);
    y += BAND_H + 10;
    for (const t of b.tables) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...BLUE);
      doc.text(t.caption.toUpperCase(), M.left, y + 6);
      y += 10;
      const widths = widthsOf(t);
      autoTable(doc, {
        startY: y,
        margin: { left: M.left, right: M.right, top: M.top, bottom: M.bottom },
        tableWidth: full,
        head: [t.head],
        body: t.body,
        foot: [t.foot],
        showFoot: 'lastPage',
        theme: 'grid',
        rowPageBreak: 'avoid',
        styles: { font: 'helvetica', fontSize: fs, cellPadding: PAD, lineColor: LINE, lineWidth: 0.5, textColor: INK, valign: 'middle', overflow: 'linebreak' },
        headStyles: { fillColor: HEAD_FILL, textColor: NAVY, fontStyle: 'bold', lineColor: LINE, lineWidth: 0.5 },
        bodyStyles: { fillColor: [255, 255, 255] },
        footStyles: { fillColor: FOOT_FILL, textColor: INK, fontStyle: 'bold', lineColor: LINE, lineWidth: 0.5 },
        columnStyles: Object.fromEntries(widths.map((w, i) => [i, { cellWidth: w }])),
        // Month on the left; every figure, its heading and its Total on one right edge ([PDF-HEAD-ALIGN 2026-09-30]).
        didParseCell: (d: CellHookData) => { d.cell.styles.halign = d.column.index === 0 ? 'left' : 'right'; },
      });
      y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 14;
    }
    y += 10;
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(`Page ${i} of ${pages}`, pageW / 2, pageH - 22, { align: 'center' });
  }
  return { name: `${p.base}.pdf`, doc };
}

/** Make the file and save it. False when no rep has data in these months (nothing is saved). Throws when a part of the
 *  data could not be read. `picked` = the reps ticked in the pop-up (all when left out). */
export function downloadMySalesReport(report: MySalesReport, format: MySalesReportFormat, picked?: string[]): boolean {
  if (format === 'xlsx') {
    const file = buildMySalesXlsx(report, picked);
    if (!file) return false;
    downloadXlsx(file.name, file.bytes);
    return true;
  }
  const file = buildMySalesPdf(report, picked);
  if (!file) return false;
  file.doc.save(file.name);
  return true;
}
